import {
  collectionGroup,
  getDocFromServer,
  getDocsFromServer,
  query,
  where,
} from "firebase/firestore";
import { paths } from "@/data/firestore/paths";
import { db } from "@/lib/firebase-firestore";
import { currentManagerUid, isActiveManager } from "@/domain/membership";

export type ManagedCompany = { customerId: string; name: string; domain: string };

export async function findManagedCompanies(uid: string): Promise<ManagedCompany[]> {
  const memberships = await getDocsFromServer(
    query(collectionGroup(db, "members"), where("uid", "==", uid)),
  );
  const customerIds = [
    ...new Set(
      memberships.docs.flatMap((member) => {
        const match = /^customers\/([^/]+)\/members\/([^/]+)$/.exec(member.ref.path);
        return match &&
          match[2] === uid &&
          member.data()["uid"] === uid &&
          isActiveManager(member.data())
          ? [match[1]!]
          : [];
      }),
    ),
  ];
  const companies = await Promise.all(
    customerIds.map(async (customerId) => {
      // A stale collection-group membership may still be discoverable, but cannot
      // authorize reading its protected company. Denial excludes that company.
      const customer = await getDocFromServer(paths.customer(customerId)).catch(
        (error: unknown) => {
          if ((error as { code?: string })?.code === "permission-denied") return null;
          throw error;
        },
      );
      const account = customer?.data();
      if (!customer?.exists() || currentManagerUid(account) !== uid) return null;
      const profile = await getDocFromServer(paths.business(customerId)).catch(() => null);
      const business = profile?.data();
      const names = business?.["name"] as Record<string, unknown> | undefined;
      const language = business?.["primaryLanguage"];
      const name =
        (typeof language === "string" && typeof names?.[language] === "string"
          ? names[language]
          : undefined) ??
        (typeof names?.["en"] === "string" ? names["en"] : undefined) ??
        Object.values(names ?? {}).find((value): value is string => typeof value === "string") ??
        customerId;
      return {
        customerId,
        name,
        domain:
          typeof account?.["normalizedDomain"] === "string" ? account["normalizedDomain"] : "",
      };
    }),
  );
  return companies.filter((company): company is ManagedCompany => company !== null);
}
